import React from 'react'
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import AgTechActionPane from '@agtech/web/components/Headers/AgTechActionPane';
import AgTechHeaderMetric from '@agtech/web/components/Metrics/AgTechHeaderMetric';
import AgTechStandardTabPage from '@agtech/web/components/Pages/Tabbed/AgTechTabPage';
import AgTechViewDialog from '@agtech/web/components/Portals/Dialogs/AgTechViewDialog';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { isLotInvoiced, SaleLotData } from '@sales/data/lots/LotData';
import { useCurrentSale } from '@sales/data/sales/SaleDataStore';
import SaleLotSellingGrid from '../components/grid/SaleLotSellingGrid';
import SaleLotViewGrid from '../components/grid/SaleLotViewGrid';
import { useLotDataGetSaleLotDataQuery } from '@sales/data/lots/LotDataQueries';
import SaleLotModificationGrid from '../components/grid/SaleLotModificationGrid';
import SaleLotImportModal from '../modals/SaleLotImportModal';
import { LotManagementDialogTourConfig } from './LotManagementDialogTourConfig';

const LotManagementDialog = () => {
    const currentSale = useCurrentSale();
    const getLotDataQuery = useLotDataGetSaleLotDataQuery();

    return (
        <AgTechViewDialog
            data={{
                query: getLotDataQuery
            }}
            structure={{
                header: (data) => ({
                    header: `Manage Sale Lots`,
                    detail: `Sale - ${currentSale.saleName}`,
                    settings: {
                        content: {
                            body: () => (
                                <div className='tall column-based-vertically-centered flex'>
                                    <LotManagementViewDialogHeader {...data} />
                                    <AgTechTourHelpButton tourConfig={LotManagementDialogTourConfig} classes='ml-4' />
                                </div>
                            )
                        }
                    }
                }),
                width: '1200px',
            }}
        >
            {(lotData) => (
                <div className='p-body flexed flex'>
                    <AgTechStandardTabPage
                        tabs={[
                            {
                                id: 'lot-management-view-lots-tab',
                                text: 'View Lots',
                                content: () => <SaleLotViewGrid />
                            },
                            {
                                id: 'lot-management-update-lots-tab',
                                text: 'Update Lots',
                                content: () => <SaleLotModificationGrid />
                            },
                            {
                                id: 'lot-management-sell-lots-tab',
                                text: 'Sell Lots',
                                content: () => <SaleLotSellingGrid lotData={lotData} />
                            }
                        ]}
                    />
                </div>
            )}
        </AgTechViewDialog>
    )
}

const LotManagementViewDialogHeader = (props: SaleLotData) => {
    const saleLots = Object.values(props.lots);
    const lotsInvoiced = saleLots.filter(lt => isLotInvoiced(lt));
    const lotsSold = saleLots.filter(lt => lt.bidderNumber && lt.purchasePrice);

    return (
        <div id='lot-management-metrics'>
            <AgTechActionPane
                elements={[
                    <AgTechHeaderMetric
                        metric='Total Lots'
                        value={saleLots.length.toString()}
                        settings={{ classes: 'px-2' }}
                    />,
                    <AgTechHeaderMetric
                        metric='Lots Sold'
                        value={lotsSold.length.toString()}
                        settings={{ classes: 'px-2' }}
                    />,
                    <AgTechHeaderMetric
                        metric='Lots Invoiced'
                        value={lotsInvoiced.length.toString()}
                        settings={{ classes: 'px-2' }}
                    />,
                    <div id='lot-management-import-btn'>
                        <SaleLotImportModal.Button classes='button-highlight' />
                    </div>
                ]}
            />
        </div>
    )
}

LotManagementDialog.Open = (e: AgTechClickEventHandler) => e.dialogs.openDialog({
    dialog: () => <LotManagementDialog />
})

LotManagementDialog.Button = (props: AgTechButtonProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Manage Lots',
        icon: 'fas fa-edit',
        ...props,
        action: LotManagementDialog.Open
    };

    return <AgTechButton {...buttonProps} />;
}

export default LotManagementDialog
